import api from '../services/api';

const getFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;

  const encodedMatch = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i
  );
  if (encodedMatch) {
    const encodedFilename = encodedMatch[1].trim().replace(/^["']|["']$/g, '');
    try {
      return decodeURIComponent(encodedFilename);
    } catch {
      return encodedFilename;
    }
  }

  const filenameMatch = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i
  );
  return filenameMatch?.[1] || filenameMatch?.[2]?.trim() || null;
};

/**
 * Download an image file automatically (no browser redirect)
 * @param {Object} image - Image object with file_path, s3_url, id, filename
 * @param {Function} onProgress - Optional progress callback
 * @param {Function} onError - Optional error callback
 */
export const downloadImage = async (image, onProgress = null, onError = null) => {
  let objectUrl = null;
  let link = null;

  try {
    // Show loading state if progress callback provided
    if (onProgress) onProgress('Starting download...', 0);

    const response = await api.get(
      `/serve/image/${encodeURIComponent(image.id)}`,
      {
        params: { download: true },
        responseType: 'blob',
      }
    );

    objectUrl = URL.createObjectURL(response.data);
    link = document.createElement('a');
    link.href = objectUrl;
    link.download =
      getFilenameFromContentDisposition(response.headers?.['content-disposition']) ||
      image.filename ||
      `image_${image.id}.jpg`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    if (onProgress) onProgress('Download started', 100);
  } catch (error) {
    console.error('Error downloading image:', error);
    if (onError) onError(error);
  } finally {
    if (link?.parentNode) link.parentNode.removeChild(link);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Download multiple images as a zip file (future enhancement)
 * @param {Array} images - Array of image objects
 * @param {string} zipName - Name for the zip file
 */
export const downloadImagesAsZip = async (images, zipName = 'images.zip') => {
  // This would require a zip library like JSZip
  // For now, download images individually
  for (const image of images) {
    await downloadImage(image);
    // Add small delay between downloads to avoid overwhelming the browser
    await new Promise(resolve => setTimeout(resolve, 500));
  }
};

/**
 * Get file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
